/**
 * Client-side subscription plan helpers (mirrors API logic).
 * Legacy companies without subscriptionPlanDetails remain fully active.
 */

import { decodeToken } from 'react-jwt';
import { AESDecryption } from './Aes';
import { notifySubscriptionRestriction } from '../components/SubscriptionRestrictionFeedback';

export const CONTACT_GROUP_ADMIN_MESSAGE =
  'Contact your group admin to update your package';

export const resolveAuthToken = (token) => {
  if (token) {
    return token;
  }
  const encryptedToken = localStorage.getItem('token');
  if (!encryptedToken) {
    return null;
  }
  try {
    return AESDecryption(encryptedToken) || null;
  } catch {
    return null;
  }
};

export const getRoleNameFromToken = (token) => {
  try {
    const authToken = resolveAuthToken(token);
    if (!authToken) return '';
    return decodeToken(authToken)?.role?.name || '';
  } catch {
    return '';
  }
};

export const getIsSoPortalGroupAdminFromToken = (token) => {
  try {
    const authToken = resolveAuthToken(token);
    if (!authToken) return false;
    return decodeToken(authToken)?.isSoPortalGroupAdmin === true;
  } catch {
    return false;
  }
};

/** System Admin (env URL) or Group Admin flagged for Self Onboarding Portal (manage-profile API). */
export const canAccessSelfOnboardingPortal = (roleName, token) => {
  const authToken = resolveAuthToken(token);
  const resolvedRole = String(roleName ?? getRoleNameFromToken(authToken) ?? '').trim();
  if (resolvedRole === 'System Admin') {
    return true;
  }
  if (resolvedRole === 'Group Admin') {
    return getIsSoPortalGroupAdminFromToken(authToken);
  }
  return false;
};

export const shouldShowSelfOnboardingPortalLink = (
  roleName,
  subscriptionPlanDetails = null,
  token
) => {
  const authToken = resolveAuthToken(token);
  const resolvedRole = roleName ?? getRoleNameFromToken(authToken);
  if (resolvedRole === 'System Admin') {
    return canAccessSelfOnboardingPortal(resolvedRole, authToken) &&
      Boolean(getSelfOnboardingPortalUrl());
  }
  if (resolvedRole === 'Group Admin') {
    return (
      Boolean(subscriptionPlanDetails) &&
      getIsSoPortalGroupAdminFromToken(authToken)
    );
  }
  return false;
};

export const SUBSCRIPTION_REMINDER_WINDOW_DAYS = 30;

/** Days Mogu retains a company after subscription expiry before permanent deletion. */
export const SUBSCRIPTION_POST_EXPIRY_RETENTION_DAYS = 30;

/** Seconds before auto-logout after subscription expires mid-session (non–System Admin). */
export const SUBSCRIPTION_LOGOUT_GRACE_SECONDS = Number(
  process.env.REACT_APP_SUBSCRIPTION_LOGOUT_GRACE_SECONDS || 60
);

let lastSubscriptionNotifyAt = 0;

export const SUBSCRIPTION_EXPIRED_READ_ONLY_SUMMARY =
  "Your company's subscription has ended. You can still sign in and review data, but changes to the portal are temporarily restricted.";

export const SUBSCRIPTION_EXPIRED_RETENTION_NOTICE =
  `Mogu retains this company for up to ${SUBSCRIPTION_POST_EXPIRY_RETENTION_DAYS} days after expiry. Renew or upgrade before then to avoid permanent deletion of the company and all its data.`;

export const SUBSCRIPTION_EXPIRED_MESSAGE = `${SUBSCRIPTION_EXPIRED_READ_ONLY_SUMMARY} ${SUBSCRIPTION_EXPIRED_RETENTION_NOTICE}`;

export const isSubscriptionBlockedError = (err) =>
  Boolean(
    err?.isSubscriptionBlocked ||
      err?.code === 'SUBSCRIPTION_READ_ONLY' ||
      (err?.response?.status === 403 &&
        String(err?.response?.data?.message || '')
          .toLowerCase()
          .includes('subscription'))
  );

export const getSubscriptionExpiredNotificationMessage = (roleName, token) => {
  const authToken = resolveAuthToken(token);
  const resolvedRole = roleName ?? getRoleNameFromToken(authToken);
  if (canAccessSelfOnboardingPortal(resolvedRole, authToken)) {
    return SUBSCRIPTION_EXPIRED_MESSAGE;
  }
  return `${SUBSCRIPTION_EXPIRED_READ_ONLY_SUMMARY} ${CONTACT_GROUP_ADMIN_MESSAGE}`;
};

export const resolveSubscriptionRestrictionMessage = (message, roleName, token) => {
  const authToken = resolveAuthToken(token);
  const resolvedRole = roleName ?? getRoleNameFromToken(authToken);
  if (canAccessSelfOnboardingPortal(resolvedRole, authToken)) {
    return message;
  }
  if (!message) {
    return CONTACT_GROUP_ADMIN_MESSAGE;
  }
  if (/self-onboarding portal/i.test(message)) {
    return message.replace(
      /\.?\s*please visit Manage Subscription[^.]*\.?/gi,
      `. ${CONTACT_GROUP_ADMIN_MESSAGE}`
    );
  }
  return `${message} ${CONTACT_GROUP_ADMIN_MESSAGE}`;
};

/** Single debounced bottom-left warning (avoids duplicate top + side toasts). */
export const notifySubscriptionBlocked = (
  description,
  options = {}
) => {
  const authToken = resolveAuthToken(options.token);
  const roleName = options.roleName ?? getRoleNameFromToken(authToken);
  const message = description
    ? description === SUBSCRIPTION_EXPIRED_MESSAGE &&
      !canAccessSelfOnboardingPortal(roleName, authToken)
      ? getSubscriptionExpiredNotificationMessage(roleName, authToken)
      : resolveSubscriptionRestrictionMessage(description, roleName, authToken)
    : getSubscriptionExpiredNotificationMessage(roleName, authToken);
  const now = Date.now();
  if (now - lastSubscriptionNotifyAt < 3000) return;
  lastSubscriptionNotifyAt = now;
  notifySubscriptionRestriction({
    title: 'Subscription ended',
    message,
    variant: 'blocked',
    showPortalLink: shouldShowSelfOnboardingPortalLink(
      roleName,
      options.subscriptionPlanDetails,
      authToken
    ),
  });
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const getSelfOnboardingPortalUrl = () =>
  process.env.REACT_APP_SO_URL?.trim() ||
  process.env.REACT_APP_SELF_ONBOARDING_PORTAL_URL?.trim() ||
  '';

/** Coerce API / legacy values into something Date can parse. */
export const normalizeSubscriptionDateValue = (value) => {
  if (value == null) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value === 'object') {
    if (typeof value.toISOString === 'function') return value.toISOString();
    if (value.$date) return value.$date;
    if (Object.keys(value).length === 0) return null;
  }
  return null;
};

/**
 * Billing period dates are persisted as ISO strings with a UTC suffix (Z), but the
 * clock values are wall-clock times (14:35 means 14:35 local, not 14:35 UTC).
 * Rebuild the instant using UTC date parts in the browser's local timezone.
 */
export const getSubscriptionEndTimestamp = (endDate) => {
  const normalized = normalizeSubscriptionDateValue(endDate);
  if (!normalized) return null;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
    parsed.getUTCHours(),
    parsed.getUTCMinutes(),
    parsed.getUTCSeconds(),
    parsed.getUTCMilliseconds()
  ).getTime();
};

export const getSubscriptionRemainingMs = (endDate) => {
  const endMs = getSubscriptionEndTimestamp(endDate);
  if (endMs === null) return null;
  return endMs - Date.now();
};

/** Full 24-hour periods remaining (floor). Under 24h remaining returns 0 — use countdown for exact time. */
export const getDaysUntilSubscriptionEnd = (endDate) => {
  const remainingMs = getSubscriptionRemainingMs(endDate);
  if (remainingMs === null) return null;
  if (remainingMs <= 0) return 0;
  return Math.floor(remainingMs / MS_PER_DAY);
};

/** Human-readable label aligned with the live countdown (not rounded up to the next day). */
export const getSubscriptionRemainingLabel = (remainingMs) => {
  if (remainingMs === null) return null;
  if (remainingMs <= 0) return 'Expired';

  const days = Math.floor(remainingMs / MS_PER_DAY);
  if (days === 0) return 'Expires today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
};

export const formatSubscriptionCountdown = (remainingMs) => {
  if (remainingMs === null) return '';
  if (remainingMs <= 0) return '00:00:00';

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value) => String(value).padStart(2, '0');

  if (days > 0) {
    return `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  }

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

export const formatLogoutCountdown = (secondsRemaining) => {
  if (secondsRemaining == null || secondsRemaining < 0) return '00:00';
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const isCompanySubscriptionActive = (
  subscriptionPlanDetails,
  subscriptionExpired = false
) => {
  if (subscriptionExpired === true) {
    return false;
  }

  if (!subscriptionPlanDetails) {
    return true;
  }

  const endDate = normalizeSubscriptionDateValue(
    subscriptionPlanDetails?.billingPeriod?.endDate
  );
  if (!endDate) {
    return true;
  }

  const endMs = getSubscriptionEndTimestamp(endDate);
  if (endMs === null) {
    return true;
  }

  return endMs >= Date.now();
};

/** Whether a company record is in subscription read-only mode (except System Admin). */
export const isCompanySubscriptionExpired = (
  companyOrPlanDetails,
  subscriptionExpired
) => {
  const planDetails =
    companyOrPlanDetails?.subscriptionPlanDetails ?? companyOrPlanDetails;
  const expiredFlag =
    subscriptionExpired ?? companyOrPlanDetails?.subscriptionExpired ?? false;

  return !isCompanySubscriptionActive(planDetails, expiredFlag);
};

/** Whether periodic quota polling should run (active subscription, non-exempt company selected). */
export const shouldPollSubscriptionQuota = ({
  subscriptionExempt = false,
  subscriptionPlanExpired = false,
  subscriptionReadOnly = false,
  subscriptionHasPlan = false,
  subscriptionCompanyId = null,
} = {}) => {
  if (subscriptionExempt) {
    return false;
  }
  if (
    !subscriptionCompanyId ||
    subscriptionCompanyId === 'Select Company'
  ) {
    return false;
  }
  if (!subscriptionHasPlan) {
    return false;
  }
  if (subscriptionPlanExpired || subscriptionReadOnly) {
    return false;
  }
  return true;
};

/** Poll while expired so external plan renewals clear the banner without a full page refresh. */
export const shouldPollSubscriptionRenewalCheck = ({
  subscriptionExempt = false,
  subscriptionHasPlan = false,
  subscriptionCompanyId = null,
  subscriptionPlanExpired = false,
} = {}) => {
  if (subscriptionExempt) {
    return false;
  }
  if (!subscriptionHasPlan) {
    return false;
  }
  if (
    !subscriptionCompanyId ||
    subscriptionCompanyId === 'Select Company'
  ) {
    return false;
  }
  return subscriptionPlanExpired;
};

/** Single gate for layout-level subscription polling (quota sync + expired renewal). */
export const shouldPollSubscriptionState = ({
  subscriptionExempt = false,
  subscriptionPlanExpired = false,
  subscriptionReadOnly = false,
  subscriptionHasPlan = false,
  subscriptionCompanyId = null,
} = {}) =>
  shouldPollSubscriptionQuota({
    subscriptionExempt,
    subscriptionPlanExpired,
    subscriptionReadOnly,
    subscriptionHasPlan,
    subscriptionCompanyId,
  }) ||
  shouldPollSubscriptionRenewalCheck({
    subscriptionExempt,
    subscriptionPlanExpired,
    subscriptionHasPlan,
    subscriptionCompanyId,
  });

export const isSubscriptionExpiringSoon = (
  subscriptionPlanDetails,
  windowDays = SUBSCRIPTION_REMINDER_WINDOW_DAYS
) => {
  if (!subscriptionPlanDetails) {
    return false;
  }
  if (!isCompanySubscriptionActive(subscriptionPlanDetails)) {
    return false;
  }

  const endDate = normalizeSubscriptionDateValue(
    subscriptionPlanDetails?.billingPeriod?.endDate
  );
  const remainingMs = getSubscriptionRemainingMs(endDate);
  if (remainingMs === null || remainingMs <= 0) {
    return false;
  }

  return remainingMs <= windowDays * MS_PER_DAY;
};

export const getCompanySubscriptionStatus = (
  subscriptionPlanDetails,
  subscriptionExpired = false
) => {
  const hasPlan = Boolean(subscriptionPlanDetails);
  const isActive = isCompanySubscriptionActive(
    subscriptionPlanDetails,
    subscriptionExpired
  );
  const endDate = normalizeSubscriptionDateValue(
    subscriptionPlanDetails?.billingPeriod?.endDate
  );
  const startDate = normalizeSubscriptionDateValue(
    subscriptionPlanDetails?.billingPeriod?.startDate
  );
  const daysUntilExpiry = endDate ? getDaysUntilSubscriptionEnd(endDate) : null;
  const isExpiringSoon =
    hasPlan && isActive && isSubscriptionExpiringSoon(subscriptionPlanDetails);

  return {
    hasPlan,
    isActive,
    isExpired: hasPlan && !isActive,
    isExpiringSoon,
    daysUntilExpiry,
    endDate,
    startDate,
  };
};

export const formatSubscriptionEndDateTime = (endDate) => {
  const normalized = normalizeSubscriptionDateValue(endDate);
  if (!normalized) return '';
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return '';

  const wallClockEnd = new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
    parsed.getUTCHours(),
    parsed.getUTCMinutes(),
    parsed.getUTCSeconds()
  );

  try {
    return wallClockEnd.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(endDate);
  }
};

export const formatSubscriptionEndDate = (endDate) => formatSubscriptionEndDateTime(endDate);

export const buildSubscriptionExpiringSoonBannerMessage = (endDate, roleName, token) => {
  const formatted = formatSubscriptionEndDateTime(endDate);
  const authToken = resolveAuthToken(token);
  const resolvedRole = roleName ?? getRoleNameFromToken(authToken);
  if (canAccessSelfOnboardingPortal(resolvedRole, authToken)) {
    return `Your subscription will end on ${formatted}. Please visit Manage Subscription to extend your plan.`;
  }
  return `Your subscription will end on ${formatted}. ${CONTACT_GROUP_ADMIN_MESSAGE}`;
};

export const buildSubscriptionExpiringSoonDashboardMessage = (endDate, roleName, token) => {
  const formatted = formatSubscriptionEndDateTime(endDate);
  const authToken = resolveAuthToken(token);
  const resolvedRole = roleName ?? getRoleNameFromToken(authToken);
  if (canAccessSelfOnboardingPortal(resolvedRole, authToken)) {
    return `Your subscription will expire on ${formatted}. Please visit Manage Subscription to renew or extend your plan.`;
  }
  return `Your subscription will expire on ${formatted}. ${CONTACT_GROUP_ADMIN_MESSAGE}`;
};

export const getSubscriptionBoosterPlanHint = (roleName, token) => {
  const authToken = resolveAuthToken(token);
  const resolvedRole = roleName ?? getRoleNameFromToken(authToken);
  if (!canAccessSelfOnboardingPortal(resolvedRole, authToken)) {
    return CONTACT_GROUP_ADMIN_MESSAGE;
  }
  return 'Purchase a booster plan or upgrade your package in Manage Subscription to continue.';
};

export const getDocumentUploadBoosterPlanHint = (roleName, token) => {
  const authToken = resolveAuthToken(token);
  const resolvedRole = roleName ?? getRoleNameFromToken(authToken);
  if (!canAccessSelfOnboardingPortal(resolvedRole, authToken)) {
    return CONTACT_GROUP_ADMIN_MESSAGE;
  }
  return 'Purchase a booster plan or upgrade your package in Manage Subscription to upload more documents.';
};

export const SUBSCRIPTION_EXPIRED_ADMIN_MESSAGE =
  "This company's subscription has ended. Other users are in read-only mode; you retain full access as System Admin. " +
  SUBSCRIPTION_EXPIRED_RETENTION_NOTICE;

/** Row action menu keys blocked when subscription is in read-only mode. */
export const SUBSCRIPTION_BLOCKED_ACTION_KEYS = new Set([
  'individualSubmit',
  'share',
  'sendViaPeppol',
  'DeleteInvoice',
  'cancel',
  'rn',
  'creditNote',
  'debitNote',
]);

export const filterSubscriptionMenuItems = (items, canPerformDocActions) =>
  items.filter(
    (item) =>
      item && (canPerformDocActions || !SUBSCRIPTION_BLOCKED_ACTION_KEYS.has(item.key))
  );

export default {
  isCompanySubscriptionActive,
  isSubscriptionExpiringSoon,
  shouldPollSubscriptionQuota,
  shouldPollSubscriptionRenewalCheck,
  shouldPollSubscriptionState,
  getCompanySubscriptionStatus,
  formatSubscriptionEndDate,
  buildSubscriptionExpiringSoonBannerMessage,
  buildSubscriptionExpiringSoonDashboardMessage,
  getSelfOnboardingPortalUrl,
  getSubscriptionBoosterPlanHint,
  getDocumentUploadBoosterPlanHint,
  CONTACT_GROUP_ADMIN_MESSAGE,
  canAccessSelfOnboardingPortal,
  getIsSoPortalGroupAdminFromToken,
  resolveAuthToken,
  shouldShowSelfOnboardingPortalLink,
  SUBSCRIPTION_EXPIRED_MESSAGE,
};
